// Waiting on the world: bounded retry loops an agent wrote to block on something
// outside its control.
//
// The behaviour, as it appears in real traces:
//
//   for i in $(seq 1 40); do
//     if docker ps >/dev/null 2>&1; then echo "docker UP after ~$((i*5))s"; break; fi
//     sleep 5
//   done
//   docker ps && echo READY || echo "still down"
//
// Invisible to every name-level extractor: the tool call is a shell call and the
// loop is a string argument. Yet it is one of the few places an agent writes down
// its own deadline — `seq 1 40` with `sleep 5` is a self-imposed 200-second budget
// — and one of the few where PROCEEDING ON AN ASSUMPTION is recoverable rather
// than inferred.
//
// That last point is the whole lens. A loop that breaks early prints something; a
// loop that runs to exhaustion does not. So when the loop names its own ready
// signal, the tool result says which happened, and the difference between "the
// agent observed the thing was ready" and "the agent gave up and kept going" is
// evidence rather than a guess. Where the loop breaks silently we say so instead of
// picking the flattering reading.
//
// The door is the SLEEP. `for f in *.json; do …; done` iterates; only a loop that
// pauses between attempts is waiting.

import {
  HARNESS_NOTICE_RE,
  SHELL_ERROR_RE,
  SHELL_TOOL_NAMES,
  resolveShellVars,
  splitStatements,
  stripAnsi,
  tokenize,
} from "../../src/mine/shell.js";

const OUT_CAP = 4000;
const str = (v) => (typeof v === "string" ? v : "");
const contentText = (c) => (typeof c === "string" ? c
  : Array.isArray(c) ? c.map((p) => (typeof p === "string" ? p : str(p?.text))).join("\n") : str(c?.text));

// POSIX `for|while|until … do … done`. Non-greedy: the nearest `do`/`done` pair.
// `\bdo\b` cannot match inside `docker` (a word char follows), which is the only
// near-collision in this corpus.
const POSIX_LOOP = /\b(for|while|until)\b([\s\S]{0,400}?)\bdo\b([\s\S]*?)\bdone\b/g;
// PowerShell `for (…) { … }` / `while (…) { … }`.
const PS_LOOP = /\b(for|while)\s*\(([\s\S]{0,300}?)\)\s*\{([\s\S]*?)\}/g;

const SLEEP_RE = /\bsleep\s+(\d+(?:\.\d+)?)/i;
const PS_SLEEP_SEC_RE = /\bStart-Sleep\b(?:\s+-Seconds?)?\s+(\d+(?:\.\d+)?)/i;
const PS_SLEEP_MS_RE = /\bStart-Sleep\b\s+-Milliseconds\s+(\d+)/i;

/** Seconds this loop pauses between attempts, or null when it never pauses. */
export function sleepSecondsOf(body) {
  const ms = body.match(PS_SLEEP_MS_RE);
  if (ms) return Number(ms[1]) / 1000;
  const m = body.match(SLEEP_RE) || body.match(PS_SLEEP_SEC_RE);
  return m ? Number(m[1]) : null;
}

/**
 * How many attempts the loop allows. `null` means UNBOUNDED — a `while` with no
 * counter, which is a real and different fact from a large budget: nothing in the
 * command says when the agent would stop.
 */
export function iterationsOf(header) {
  // Every bound must be literal. `seq 1 $MAX` is unbounded AS WRITTEN, and reading
  // the leading `1` as the count would report a 40-attempt loop as a single try —
  // understating a budget reads as diligence the session did not have.
  const seq = header.match(/\bseq\s+([^);|&]+)/);
  if (seq) {
    const parts = seq[1].trim().split(/\s+/);
    if (parts.every((p) => /^\d+$/.test(p))) {
      const nums = parts.map(Number);
      // `seq FIRST INCREMENT LAST` — the middle argument is the STEP, not a bound.
      // Treating it as `last - first + 1` reported `seq 1 5 40` as 40 attempts where
      // it is 8, inflating the budget figure this lens puts in front of a reader.
      if (nums.length >= 3) {
        const [first, step, last] = nums;
        if (!step || last < first) return null;
        return Math.floor((last - first) / step) + 1;
      }
      return nums.length === 2 ? nums[1] - nums[0] + 1 : nums[0];
    }
    return null;
  }
  const range = header.match(/\b(\d+)\s*\.\.\s*(\d+)\b/);
  if (range) return Number(range[2]) - Number(range[1]) + 1;
  const cmp = header.match(/-l[et]\s+(\d+)\b/);
  if (cmp) return Number(cmp[1]);
  // A literal word list — `for i in a b c` — is a fixed attempt count too.
  const words = header.match(/\bin\s+([^;{}()$]+)$/);
  if (words) {
    const n = words[1].trim().split(/\s+/).filter(Boolean).length;
    if (n > 1) return n;
  }
  return null;
}

// Ordered: first match wins. Each probe kind is a different thing to be blocked
// ON, which is what a reader is scanning for.
const PROBES = [
  ["ci-status", /\b(?:curl|Invoke-RestMethod|Invoke-WebRequest)\b[\s\S]*?(?:\/status|statuses|check-runs|actions\/(?:tasks|runs)|\bconclusion\b)/i, "a CI verdict"],
  ["port", /\/dev\/tcp\/|\bnc\s+-[a-z]*z|Test-NetConnection|\bss\s+-|\blsof\s+-i|\bwaitport\b/i, "a port to open"],
  ["database", /\bpg_isready\b|\bpsql\b|\bmysqladmin\b|redis-cli\s+ping|\bclickhouse-client\b|\bSELECT\s+1\b/i, "a database to accept connections"],
  ["container", /\bdocker\b|\bkubectl\b|\bpodman\b|\bnerdctl\b/i, "a container or pod"],
  ["process", /\bps\s+aux|\bpgrep\b|Get-Process|\btasklist\b|\bpidof\b/i, "a process"],
  ["http", /\bcurl\b|\bwget\b|Invoke-WebRequest|Invoke-RestMethod/i, "an HTTP endpoint"],
  ["file-marker", /\bgrep\s+-[a-z]*q|Select-String[\s\S]*?-Quiet|\[\s*-[fse]\s|Test-Path/i, "a marker to appear in a file"],
];

export function probeKindOf(body) {
  for (const [kind, re, phrase] of PROBES) if (re.test(body)) return { kind, phrase };
  return { kind: "custom", phrase: "a condition of its own" };
}

/**
 * The most probe-like line in the loop body, for display. The body also holds the
 * sleep and the break, which are mechanism rather than the question being asked.
 */
export function probeTextOf(body) {
  const lines = body.split(/\n|;/).map((l) => l.trim()).filter(Boolean);
  const meaningful = lines.filter((l) => !/^(?:sleep\b|Start-Sleep\b|break\b|continue\b|fi\b|done\b|else\b|\}|\{)/i.test(l));
  const scored = meaningful.map((l) => [l, PROBES.some(([, re]) => re.test(l)) ? 2 : 1]);
  scored.sort((a, b) => b[1] - a[1]);
  const best = (scored[0]?.[0] || meaningful[0] || lines[0] || "").replace(/\s+/g, " ");
  return best.length > 220 ? `${best.slice(0, 220)}…` : best;
}

// `echo "…"` / `print`/`Write-Host` with a literal. Bare-word form included:
// `echo READY` is common and carries the same signal.
const ECHO_RE = /\b(?:echo|print|printf|Write-Host|Write-Output)\s+(?:"([^"]{1,120})"|'([^']{1,120})'|([A-Za-z][\w.:-]{1,40}))/g;

/**
 * The literal an early break announces — the loop's own "I saw it" signal.
 *
 * Only echoes adjacent to a `break` count. An echo AFTER the loop runs either way,
 * so treating it as a ready signal would report every exhausted loop as a success.
 */
export function readyMarkersOf(body) {
  const echoes = [];
  ECHO_RE.lastIndex = 0;
  for (let m = ECHO_RE.exec(body); m; m = ECHO_RE.exec(body)) {
    echoes.push({ at: m.index, text: m[1] || m[2] || m[3] || "" });
  }
  const markers = [];
  for (const bm of body.matchAll(/\bbreak\b/g)) {
    // Nearest preceding echo within the same clause. `{ echo "pg READY"; break; }`
    // and `then echo "…"; break` are the two spellings that occur.
    const near = echoes.filter((e) => e.at < bm.index && bm.index - e.at < 200).pop();
    if (near) markers.push(near.text);
  }
  return [...new Set(markers.map(constantPrefix).filter((s) => s.length >= 3))];
}

/**
 * The part of a marker that survives interpolation. `docker UP after ~$((i*5))s`
 * only ever appears in output with its prefix intact, so matching the whole
 * literal would miss every marker that reports how long it waited.
 */
export function constantPrefix(literal) {
  return String(literal).split(/[$`%]/)[0].replace(/\s+/g, " ").trim();
}

/**
 * The condition an early exit tests, when it tests a variable against a literal:
 * `[ "$n" = "0" ] && break`, `if [ "$ST" = "success" ]; then … break`.
 *
 * Worth capturing on its own — it is the loop's definition of "ready" — and it is
 * also half of the post-mortem recovery below.
 */
export function breakConditionOf(body) {
  const idx = body.search(/\bbreak\b/);
  const before = idx === -1 ? body : body.slice(Math.max(0, idx - 300), idx);
  const m = [...before.matchAll(/\[+\s*"?\$\{?(\w+)\}?"?\s*(?:=|==|-eq|!=|-ne)\s*"?([\w.-]+)"?\s*\]+/g)].pop();
  return m ? { var: m[1], value: m[2], negated: /!=|-ne/.test(m[0]) } : null;
}

// `echo "done waiting (procs=$n)"` — a label bound to a variable. The label is what
// reaches the output; the variable is what the loop was testing.
const LABELLED_RE = /([A-Za-z][\w -]{0,24})=\$\{?(\w+)\}?/g;

/** Label→variable bindings printed after the loop: its own post-mortem. */
export function postMortemBindings(tail) {
  const out = [];
  for (const m of String(tail).matchAll(LABELLED_RE)) out.push({ label: m[1].trim(), var: m[2] });
  return out;
}

/**
 * The pattern a `grep -q`-style probe is looking FOR. Skipped when the grep is
 * INVERTED: `grep -vE "x"` waits for x to be absent, so treating x's presence as
 * readiness inverts the answer. (An inverted filter read the right-way-round is a
 * mistake this codebase has already made once.)
 */
export function probePatternOf(body) {
  const m = /\b(?:grep|egrep|rg)\s+(-[A-Za-z]*)\s*(?:-[eE]\s+)?["']([^"']{2,120})["']|Select-String\s+(?:-Pattern\s+)?["']([^"']{2,120})["']/.exec(body);
  if (!m) return [];
  if (m[1] && /v/.test(m[1])) return [];
  if (/-NotMatch/i.test(body)) return [];
  return String(m[2] || m[3] || "")
    .split("|")
    .map((s) => s.trim())
    // Only literal alternatives. A fragment carrying regex metacharacters would
    // not match the output as plain text anyway, and guessing at its semantics is
    // how a lens starts reporting things it cannot see.
    .filter((s) => s.length >= 3 && /^[\w .:/-]+$/.test(s));
}

/**
 * Did the loop resolve? Read off the output, never inferred from exit status.
 *
 *   observed-ready  the break's own announcement is in the output
 *   condition-met   the loop's readiness pattern is present in what the trace read
 *                   back, though the loop never announced an exit. Weaker than the
 *                   above — the pattern could have landed just after the last
 *                   attempt — so it is named separately rather than folded in.
 *   gave-up         the loop named a ready signal and the output carries none
 *   unrecoverable   nothing in the trace distinguishes the two
 *
 * Two recovery routes, in order of directness. The second exists because the most
 * common spelling in real traces breaks SILENTLY — `[ "$n" = "0" ] && break` — and
 * then prints the tested variable afterwards: `done waiting (procs=$n)` reaching
 * the output as `done waiting (procs=0)` says the condition held. That is a
 * derivation from the loop's own text, not a charitable guess; without it 44% of
 * waits would be reported as unanswerable while the answer sat in the output.
 */
export function outcomeOf({ markers, breakWhen, bindings, probeFor = [] }, output) {
  if (!output) return { outcome: "unrecoverable", readyLine: null };

  for (const line of output.split("\n")) {
    if (markers.length && markers.some((m) => line.includes(m))) return ready(line);
  }

  if (breakWhen && bindings.length) {
    const bound = bindings.filter((b) => b.var === breakWhen.var);
    for (const b of bound) {
      const hit = new RegExp(`${b.label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*=\\s*"?([\\w.-]+)`).exec(output);
      if (!hit) continue;
      const held = hit[1] === breakWhen.value;
      // A negated test (`!= ""`) reads the other way round, so honour it rather
      // than assuming equality means ready.
      return (breakWhen.negated ? !held : held)
        ? ready(output.split("\n").find((l) => l.includes(hit[0])) || hit[0])
        : { outcome: "gave-up", readyLine: null };
    }
  }

  if (probeFor.length) {
    for (const line of output.split("\n")) {
      const hit = probeFor.find((p) => line.includes(p));
      if (hit) {
        const t = line.trim();
        return { outcome: "condition-met", readyLine: t.length > 200 ? `${t.slice(0, 200)}…` : t };
      }
    }
  }

  if (markers.length) return { outcome: "gave-up", readyLine: null };
  return { outcome: "unrecoverable", readyLine: null };
}

function ready(line) {
  const t = String(line).trim();
  return { outcome: "observed-ready", readyLine: t.length > 200 ? `${t.slice(0, 200)}…` : t };
}

/** What the agent did once it stopped waiting — the reason it waited at all. */
export function payoffOf(tail) {
  const progs = [];
  for (const st of splitStatements(tail)) {
    const toks = tokenize(st.text.trim());
    let i = 0;
    while (i < toks.length && (/^\$?\w+=/.test(toks[i]) || /^&&?$/.test(toks[i]) || /^(?:export|set|cd|time|then|else|fi)$/i.test(toks[i]))) i += 1;
    const prog = (toks[i] || "").split(/[\\/]/).pop().replace(/\.exe$/i, "");
    if (prog && /^[A-Za-z][\w.-]*$/.test(prog) && !progs.includes(prog)) progs.push(prog);
    if (progs.length >= 4) break;
  }
  return progs;
}

/**
 * Character ranges holding heredoc bodies. A loop inside one is being WRITTEN to a
 * file, not run — the audit turned up a session that authors `poll_pr62.sh` and
 * invokes it separately, and reporting that as an unresolved wait blamed this call
 * for an answer that lives with the script's own run.
 */
function heredocRanges(text) {
  const ranges = [];
  for (const m of text.matchAll(/<<-?\s*['"]?(\w+)['"]?\r?\n/g)) {
    const from = m.index + m[0].length;
    const end = new RegExp(`^\\s*${m[1]}\\s*$`, "m").exec(text.slice(from));
    ranges.push([from, end ? from + end.index : text.length]);
  }
  return ranges;
}

/**
 * Is this loop header a readiness CONDITION rather than an iterator or a spin?
 * `while true` and a `for` list say nothing about what is being waited for.
 */
function isCondition(header) {
  // Trailing `;` is part of the shell's grammar, not the condition: without
  // stripping it `while true;` reads as a real test and every spin loop comes back.
  const h = String(header).trim().replace(/;+\s*$/, "");
  if (!h) return false;
  return !/^!?\s*(?:true|:|\[\s*1\s*\])\s*$/.test(h);
}

function loopsIn(text) {
  const found = [];
  const heredocs = heredocRanges(text);
  for (const re of [POSIX_LOOP, PS_LOOP]) {
    re.lastIndex = 0;
    for (let m = re.exec(text); m; m = re.exec(text)) {
      const body = m[3] || "";
      // THE DOOR: a loop that never pauses is iterating, not waiting.
      if (sleepSecondsOf(body) == null) continue;
      const at = m.index;
      found.push({
        kw: (m[1] || "").toLowerCase(),
        header: m[2] || "",
        body,
        tail: text.slice(at + m[0].length),
        mode: heredocs.some(([a, b]) => at >= a && at < b) ? "scripted" : "inline",
      });
    }
    if (found.length) break;
  }
  return found;
}

export function extractWaitOps(turns) {
  const ops = [];
  if (!Array.isArray(turns)) return ops;
  const resultById = new Map();
  for (const t of turns) {
    for (const b of t?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id) resultById.set(b.tool_use_id, b);
    }
  }

  for (const t of turns) {
    for (const b of t?.blocks || []) {
      if (b?.type !== "tool_use") continue;
      if (!SHELL_TOOL_NAMES.test(String(b.name || "").split("__").pop())) continue;
      const cmd = str(b.input?.command) || str(b.input?.cmd) || str(b.input?.script);
      if (!cmd) continue;
      const text = resolveShellVars(stripAnsi(cmd));

      const loops = loopsIn(text);
      if (!loops.length) continue;

      const result = b.id ? resultById.get(b.id) : null;
      const errored = Boolean(result?.isError) || (result ? contentText(result.content).includes("<tool_use_error>") : false);
      const rawOut = !errored && result ? contentText(result.content) : "";
      const outputStatus = !rawOut ? "none"
        : HARNESS_NOTICE_RE.test(rawOut) ? "backgrounded"
        : SHELL_ERROR_RE.test(rawOut) ? "shell-error"
        : "observed";
      const out = outputStatus === "observed" ? stripAnsi(rawOut) : "";

      loops.forEach((loop, n) => {
        // A `while`/`until` puts its readiness test in the HEADER, not the body:
        // `while ! curl -sf health; do sleep 3; done` is one of the commonest wait
        // idioms there is, and a body-only reading sees nothing but the sleep.
        const cond = (loop.kw === "while" || loop.kw === "until") && isCondition(loop.header)
          ? loop.header.trim().replace(/\s+/g, " ")
          : "";
        const bodyProbe = probeTextOf(loop.body);
        const bodyIsBare = !bodyProbe || SLEEP_RE.test(bodyProbe) || PS_SLEEP_SEC_RE.test(bodyProbe);
        const probe = bodyIsBare ? cond : bodyProbe;
        // THE SECOND HALF OF THE DOOR. With no readiness test anywhere there is
        // nothing this loop is waiting FOR — `while true; do sleep 2; done` tailing
        // a log is a pause. Classifying those produced a group reading "waiting on
        // nothing in particular" eight times over, a row that costs a reader
        // attention and returns nothing. Dropping them can empty the panel, and an
        // empty panel is the right answer when a window holds no real waits.
        if (!probe) return;
        const { kind, phrase } = probeKindOf(`${cond}\n${loop.body}`);
        const iterations = iterationsOf(loop.header);
        const sleepSecs = sleepSecondsOf(loop.body);
        const markers = readyMarkersOf(loop.body);
        const breakWhen = breakConditionOf(loop.body);
        const bindings = postMortemBindings(loop.tail.slice(0, 400));
        // `while ! grep -q X` exits when X APPEARS, so its pattern reads the same
        // way as `if grep -q X; then break`. A NON-negated `while grep -q X` is the
        // opposite — it loops while X is present — so only the negated form is safe
        // to read, and the un-negated one is left alone rather than inverted.
        const probeFor = probePatternOf(/^\s*!/.test(loop.header) ? `${loop.header}\n${loop.body}` : loop.body);
        const payoff = payoffOf(loop.tail);
        // A loop being written into a script resolves when the SCRIPT runs, which is
        // a different call. Reading this call's output for its answer would report
        // every authored monitor as a wait that never resolved.
        const { outcome, readyLine } = loop.mode === "scripted"
          ? { outcome: "authored", readyLine: null }
          : outcomeOf({ markers, breakWhen, bindings, probeFor }, out);

        ops.push({
          key: loops.length > 1 ? `${b.id}#${n}` : (b.id || `synth:wait:${kind}`),
          waitingFor: kind,
          phrase,
          probe,
          mode: loop.mode,
          breakWhen: breakWhen ? `${breakWhen.var} ${breakWhen.negated ? "≠" : "="} ${breakWhen.value}` : null,
          iterations,
          sleepSecs,
          // The self-imposed deadline, in seconds. Null when the loop is
          // unbounded — which is the finding, not a gap.
          budgetSecs: iterations != null && sleepSecs != null ? Math.round(iterations * sleepSecs) : null,
          markers,
          probeFor,
          // Display projections the generic panel reads. Without them a reader sees
          // the group summary and the raw log tail but not the two facts the lens
          // is for: what was probed, and whether THIS wait resolved.
          steps: [
            probe ? { kind: "probe", cmd: probe } : null,
            readyWhen(markers, breakWhen, probeFor) ? { kind: "ready when", cmd: readyWhen(markers, breakWhen, probeFor) } : null,
            payoff.length ? { kind: "then", cmd: payoff.join(" · ") } : null,
          ].filter(Boolean),
          checks: [{
            kind: "resolution",
            tool: phrase,
            scope: [iterations != null && sleepSecs != null ? `${iterations}× every ${sleepSecs}s` : "no stated limit"],
            outcome,
          }],
          outcome,
          readyLine,
          payoff,
          output: out ? (out.length > OUT_CAP ? out.slice(0, OUT_CAP) : out) : null,
          outputStatus,
          ok: !errored,
        });
      });
    }
  }
  return ops;
}

/** The loop's own definition of ready, in whichever form it stated it. */
function readyWhen(markers, breakWhen, probeFor) {
  if (breakWhen) return `${breakWhen.var} ${breakWhen.negated ? "\u2260" : "="} ${breakWhen.value}`;
  if (probeFor.length) return probeFor.join(" or ");
  if (markers.length) return `it prints "${markers[0]}"`;
  return null;
}

/** Why this wait shows no resolution. Only one of these is the loop's own doing. */
export function describeMissing(op) {
  if (op.mode === "scripted") {
    return "this call writes the wait into a script rather than running it — whether it resolved is recorded against the script's own run";
  }
  if (op.outcome === "gave-up") {
    const named = op.markers.length ? `announced ${op.markers.length > 1 ? "a ready signal" : `"${op.markers[0]}"`} on an early exit and the output carries none`
      : `breaks when ${op.breakWhen}, and the value it reported afterwards was not that`;
    return `the loop ${named} — it ran out its ${op.budgetSecs != null ? `${op.budgetSecs}s ` : ""}budget and the agent carried on regardless`;
  }
  // Absence of EVIDENCE outranks absence of a signal. When the output never
  // reached the trace, "the loop exits silently" blames the loop's shape for a gap
  // in what was recorded — the same over-strong reading this lens exists to avoid.
  switch (op.outputStatus) {
    case "backgrounded": return "ran in the background — the wait's own output never entered this trace";
    case "shell-error": return "the captured text was the shell's error, not the loop's output";
    case "none": return "ran and captured no output";
    default: break;
  }
  if (op.outcome === "unrecoverable" && !op.markers.length && !op.breakWhen) {
    return "the loop exits silently and reports nothing afterwards, so this trace cannot say whether it saw the thing or timed out";
  }
  return null;
}
